import { useEffect, useMemo, useRef, useState } from "react";
import { Badge, Button, Field, Input, Note, Status, Stepper } from "@fwf/fwfa-design-system";
import { AGENT_PRESETS } from "@/lib/agent-configs";
import { providerLabel, workspaceDisplayName } from "@web/lib/workspace-display";
import { parseJson, normalizeProjectOptions } from "@web/lib/api";
import { filterGithubStarterKits, pickDefaultStarterKit } from "../starter-kits";
import type {
  OnboardingDraft,
  OnboardingSelection,
  ProjectOption,
  WorkspaceProvider,
  WorkspaceRef,
} from "@web/lib/onboarding";
import { workspaceKey, sanitizeProjectFolderName } from "@web/lib/onboarding";

type StarterKitManifest = {
  id: string;
  name: string;
  description: string;
  icon?: string;
  source?: "github" | "local";
  repoFullName?: string;
};

type Props = {
  draft: OnboardingDraft;
  onDraftChange: (updater: (prev: OnboardingDraft) => OnboardingDraft) => void;
  onStart: (selection: OnboardingSelection) => void | Promise<void>;
  submitting?: boolean;
  error?: string | null;
  providerMode?: WorkspaceProvider;
  userEmail?: string;
  isAdmin?: boolean;
};

/** Preferred agent display order: Claude first (recommended), then Gemini, Codex, Copilot. */
const AGENT_ORDER = ["claude", "gemini", "codex", "copilot", "opencode"] as const;



function dedupeWorkspaces(rows: WorkspaceRef[]): WorkspaceRef[] {
  const seen = new Set<string>();
  return rows.filter((row) => {
    const key = workspaceKey(row);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function describeWorkspace(row: WorkspaceRef, category: "selected" | "mine" | "project") {
  if (category === "selected") return "Currently selected workspace";
  if (category === "mine") return row.shared ? "Your shared workspace" : "Your private workspace";
  return `Shared with project ${row.projectIds.join(", ") || "unknown"}`;
}

const STEPS = [
  { label: "Agent" },
  { label: "Project" },
  { label: "Workspace" },
  { label: "Launch" },
] as const;

/* ------------------------------------------------------------------ */
/*  Animated step wrapper                                              */
/* ------------------------------------------------------------------ */

function StepPanel({
  active,
  direction,
  children,
}: {
  active: boolean;
  direction: "left" | "right" | "none";
  children: React.ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [rendered, setRendered] = useState(active);

  useEffect(() => {
    if (active) setRendered(true);
  }, [active]);

  const handleTransitionEnd = () => {
    if (!active) setRendered(false);
  };

  if (!rendered) return null;

  const translateClass = active
    ? "translate-x-0 opacity-100"
    : direction === "left"
      ? "-translate-x-8 opacity-0"
      : direction === "right"
        ? "translate-x-8 opacity-0"
        : "opacity-0";

  return (
    <div
      ref={ref}
      onTransitionEnd={handleTransitionEnd}
      className={`
        w-full transition-all duration-300 ease-out
        ${translateClass}
        ${active ? "" : "pointer-events-none absolute inset-0"}
      `}
    >
      {children}
    </div>
  );
}

function StepActionBar({
  backLabel = "Back",
  backTestId,
  continueBusy = false,
  continueBusyLabel = "Working...",
  continueDisabled = false,
  continueStretch = false,
  continueLabel = "Continue",
  continueTestId,
  onBack,
  onContinue,
}: {
  backLabel?: string;
  backTestId?: string;
  continueBusy?: boolean;
  continueBusyLabel?: string;
  continueDisabled?: boolean;
  continueStretch?: boolean;
  continueLabel?: string;
  continueTestId?: string;
  onBack?: () => void;
  onContinue: () => void;
}) {
  return (
    <div className="sticky bottom-3 z-20 mt-6 px-3 py-3">
      <div className={`fwfa-action-row ${onBack ? "justify-between" : "justify-end"}`}>
        {onBack ? (
          <Button
            variant="ghost"
            data-testid={backTestId}
            onClick={onBack}
            leftIcon={(
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.8} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18" />
              </svg>
            )}
          >
            {backLabel}
          </Button>
        ) : (
          <div className="flex-1" />
        )}
        <Button
          variant="primary"
          data-testid={continueTestId}
          disabled={continueDisabled || continueBusy}
          loading={continueBusy}
          onClick={onContinue}
          className={continueStretch ? "flex-1 justify-center" : undefined}
        >
          {continueBusy ? (
            continueBusyLabel
          ) : (
            <>
              {continueLabel}
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.8} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="m13.5 4.5 7.5 7.5-7.5 7.5m7.5-7.5H3" />
              </svg>
            </>
          )}
        </Button>
      </div>
    </div>
  );
}

function ChoiceIndicator({ selected }: { selected: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={`onboarding-choice-indicator${selected ? " is-selected" : ""}`}
    />
  );
}

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */

export default function WebOnboardingFlow({
  draft,
  onDraftChange,
  onStart,
  submitting = false,
  error,
  providerMode = "daytona",
  userEmail,
  isAdmin = false,
}: Props) {
  const [step, setStep] = useState(0);
  const [prevStep, setPrevStep] = useState(0);
  const [projects, setProjects] = useState<ProjectOption[]>([]);
  const [starterKits, setStarterKits] = useState<StarterKitManifest[]>([]);
  const [selectedWorkspace, setSelectedWorkspace] = useState<WorkspaceRef | null>(null);
  const [mine, setMine] = useState<WorkspaceRef[]>([]);
  const [projectRows, setProjectRows] = useState<WorkspaceRef[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingStarterKits, setLoadingStarterKits] = useState(false);
  const [loadingProjectRows, setLoadingProjectRows] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [vertexFlags, setVertexFlags] = useState<{ claudeVertex: boolean; geminiVertex: boolean }>({ claudeVertex: false, geminiVertex: false });
  const [portProxyDomain, setPortProxyDomain] = useState<string | null>(null);
  const [slugStatus, setSlugStatus] = useState<"idle" | "checking" | "available" | "taken" | "reserved">("idle");
  const slugCheckTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const availableProviders = useMemo<WorkspaceProvider[]>(
    () => [providerMode],
    [providerMode],
  );

  // Stable refs so the mount effect doesn't re-fire when draft/callbacks change
  const onDraftChangeRef = useRef(onDraftChange);
  onDraftChangeRef.current = onDraftChange;
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const userEmailRef = useRef(userEmail);
  userEmailRef.current = userEmail;
  const starterKitsRequestedRef = useRef(false);

  // Load the onboarding choices that are needed before the user reaches the
  // starter-project step. GitHub starter repositories are loaded lazily below.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);

    void (async () => {
      try {
        const [projectResp, mineResp, agentConfigResp] = await Promise.all([
          fetch("/api/projects", { credentials: "include" }),
          fetch("/api/workspaces/mine", { credentials: "include" }),
          fetch("/api/agent-config", { credentials: "include" }).catch(() => null),
        ]);

        const projectData = await parseJson<{ projects: unknown }>(projectResp);
        const mineData = await parseJson<{ selected: WorkspaceRef | null; workspaces: WorkspaceRef[] }>(mineResp);
        const agentConfig = agentConfigResp ? await agentConfigResp.json().catch(() => ({})) : {};

        if (cancelled) return;

        if (agentConfig.claudeVertex || agentConfig.geminiVertex) {
          setVertexFlags({
            claudeVertex: Boolean(agentConfig.claudeVertex),
            geminiVertex: Boolean(agentConfig.geminiVertex),
          });
        }
        if (agentConfig.portProxy?.domain) {
          setPortProxyDomain(agentConfig.portProxy.domain);
        }

        const nextProjects = normalizeProjectOptions(projectData.projects);
        setProjects(nextProjects);
        setSelectedWorkspace(mineData.selected || null);
        setMine(Array.isArray(mineData.workspaces) ? mineData.workspaces : []);

        // Auto-select the first (or only) project/team from auth
        if (!draftRef.current.projectId && nextProjects[0]?.id) {
          onDraftChangeRef.current((prev) => ({
            ...prev,
            projectId: nextProjects[0].id,
          }));
        }

        if (!availableProviders.includes(draftRef.current.createProvider)) {
          onDraftChangeRef.current((prev) => ({
            ...prev,
            createProvider: availableProviders[0],
          }));
        }

        // Auto-select Claude as the default agent
        if (!draftRef.current.agentId) {
          onDraftChangeRef.current((prev) => ({
            ...prev,
            agentId: "claude",
          }));
        }

        // Auto-select existing workspace owned by this user (by email match)
        const nextMine = Array.isArray(mineData.workspaces) ? mineData.workspaces : [];
        const email = userEmailRef.current;
        if (!draftRef.current.existingWorkspaceKey && email && nextMine.length > 0) {
          const ownedByUser = nextMine.find((ws) => {
            const wsEmail = typeof ws.meta?.ownerEmail === "string" ? ws.meta.ownerEmail : "";
            return wsEmail === email;
          });
          if (ownedByUser) {
            onDraftChangeRef.current((prev) => ({
              ...prev,
              workspaceMode: "existing",
              existingWorkspaceKey: workspaceKey(ownedByUser),
            }));
          }
        }
      } catch (err) {
        if (!cancelled) {
          setLoadError(err instanceof Error ? err.message : "Failed to load onboarding choices");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableProviders]);

  // GitHub starter repositories are only needed when the starter-project UI is
  // visible. Keep the request one-shot per onboarding mount and treat failure
  // as optional so the blank-project choice remains available.
  useEffect(() => {
    if (step !== 1 || starterKitsRequestedRef.current) return;
    starterKitsRequestedRef.current = true;

    let cancelled = false;
    setLoadingStarterKits(true);

    void (async () => {
      try {
        const response = await fetch("/api/starter-kits", { credentials: "include" });
        const data = await response.json().catch(() => []);
        if (cancelled) return;

        const kits = filterGithubStarterKits(Array.isArray(data) ? data as StarterKitManifest[] : []);
        setStarterKits(kits);

        const firstKit = pickDefaultStarterKit(kits);
        if (!draftRef.current.starterProject && firstKit) {
          onDraftChangeRef.current((prev) => ({
            ...prev,
            starterProject: { kind: "starter", kitId: firstKit.id, kitName: firstKit.name, keepGit: false },
          }));
        }
      } catch {
        if (!cancelled) setStarterKits([]);
      } finally {
        if (!cancelled) setLoadingStarterKits(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [step]);

  // Debounced slug availability check when project name changes
  useEffect(() => {
    const slug = sanitizeProjectFolderName(draft.projectName.trim());
    if (!slug) {
      setSlugStatus("idle");
      return;
    }
    setSlugStatus("checking");
    if (slugCheckTimer.current) clearTimeout(slugCheckTimer.current);
    slugCheckTimer.current = setTimeout(async () => {
      try {
        const resp = await fetch(`/api/preview-slugs/check/${encodeURIComponent(slug)}`, { credentials: "include" });
        if (!resp.ok) { setSlugStatus("idle"); return; }
        const data = await resp.json() as { available: boolean; reason?: string };
        if (data.available) {
          setSlugStatus("available");
        } else {
          setSlugStatus(data.reason === "reserved" ? "reserved" : "taken");
        }
      } catch {
        setSlugStatus("idle");
      }
    }, 400);
    return () => { if (slugCheckTimer.current) clearTimeout(slugCheckTimer.current); };
  }, [draft.projectName]);

  // Load project workspaces when project changes
  useEffect(() => {
    const projectId = draft.projectId?.trim();
    if (!projectId) {
      setProjectRows([]);
      return;
    }

    let cancelled = false;
    setLoadingProjectRows(true);

    void (async () => {
      try {
        const response = await fetch(`/api/workspaces/search?projectId=${encodeURIComponent(projectId)}`, {
          credentials: "include",
        });
        const data = await parseJson<{ workspaces: WorkspaceRef[] }>(response);
        if (!cancelled) {
          setProjectRows(Array.isArray(data.workspaces) ? data.workspaces : []);
        }
      } catch (err) {
        if (!cancelled) {
          setLoadError(err instanceof Error ? err.message : "Failed to load project workspaces");
        }
      } finally {
        if (!cancelled) {
          setLoadingProjectRows(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [draft.projectId]);

  const selectedProject = projects.find((project) => project.id === draft.projectId) || null;

  const selectedKey = selectedWorkspace ? workspaceKey(selectedWorkspace) : null;
  const personalWorkspaces = useMemo(
    () => dedupeWorkspaces(mine.filter((row) => workspaceKey(row) !== selectedKey)),
    [selectedKey, mine],
  );
  const sharedProjectWorkspaces = useMemo(
    () => dedupeWorkspaces(projectRows.filter((row) => workspaceKey(row) !== selectedKey)),
    [selectedKey, projectRows],
  );

  const agentReady = Boolean(draft.agentId);
  const projectNameValid = draft.projectName.trim().length > 0 && slugStatus !== "taken" && slugStatus !== "reserved";
  const starterProjectReady = Boolean(draft.starterProject) && projectNameValid;
  const workspaceReady = draft.workspaceMode === "create"
    ? Boolean(draft.createProvider)
    : Boolean(draft.existingWorkspaceKey);
  const starterKeepGit = draft.starterProject?.kind === "starter" && isAdmin
    ? Boolean(draft.starterProject.keepGit)
    : false;

  const canStart = agentReady && starterProjectReady && workspaceReady && !loading && !submitting;

  const goTo = (next: number) => {
    setPrevStep(step);
    setStep(next);
  };

  const handleSelectStarterKit = (kit: StarterKitManifest) => {
    onDraftChange((prev) => ({
      ...prev,
      starterProject: {
        kind: "starter",
        kitId: kit.id,
        kitName: kit.name,
        keepGit: prev.starterProject?.kind === "starter" && prev.starterProject.kitId === kit.id
          ? isAdmin && Boolean(prev.starterProject.keepGit)
          : false,
      },
    }));
  };

  const handleToggleKeepGit = () => {
    if (!isAdmin) return;
    onDraftChange((prev) => {
      if (prev.starterProject?.kind !== "starter") return prev;
      return {
        ...prev,
        starterProject: { ...prev.starterProject, keepGit: !prev.starterProject.keepGit },
      };
    });
  };

  const handleSelectBlank = () => {
    onDraftChange((prev) => ({
      ...prev,
      starterProject: { kind: "blank" },
    }));
  };

  const handleStart = async () => {
    if (!draft.agentId || !draft.starterProject) return;

    // Use the auto-selected project, or fall back to a synthetic one
    const project = selectedProject || { id: draft.projectId || "default", name: draft.projectId || "My Project" };
    const resolvedProjectName = draft.projectName.trim() || project.name;

    if (draft.workspaceMode === "existing") {
      const existing = [selectedWorkspace, ...personalWorkspaces, ...sharedProjectWorkspaces]
        .filter((row): row is WorkspaceRef => Boolean(row))
        .find((row) => workspaceKey(row) === draft.existingWorkspaceKey);

      if (!existing) return;

      await onStart({
        agentId: draft.agentId,
        project,
        projectName: resolvedProjectName,
        starterProject: draft.starterProject.kind === "starter"
          ? { ...draft.starterProject, keepGit: starterKeepGit }
          : draft.starterProject,
        workspace: {
          kind: "existing",
          workspace: existing,
        },
      });
      return;
    }

    await onStart({
      agentId: draft.agentId,
      project,
      projectName: resolvedProjectName,
      starterProject: draft.starterProject.kind === "starter"
        ? { ...draft.starterProject, keepGit: starterKeepGit }
        : draft.starterProject,
      workspace: {
        kind: "create",
        provider: draft.createProvider,
        shared: draft.createShared,
      },
    });
  };

  const direction = step > prevStep ? "left" : step < prevStep ? "right" : "none";

  /* ---------------------------------------------------------------- */
  /*  Workspace card (reused in step 2 and review)                     */
  /* ---------------------------------------------------------------- */

  const renderWorkspaceCard = (
    workspace: WorkspaceRef,
    category: "selected" | "mine" | "project",
  ) => {
    const key = workspaceKey(workspace);
    const selected = draft.existingWorkspaceKey === key;
    const ownerEmail = typeof workspace.meta?.ownerEmail === "string" && workspace.meta.ownerEmail.trim()
      ? workspace.meta.ownerEmail.trim()
      : "";
    return (
      <button
        key={key}
        type="button"
        onClick={() => onDraftChange((prev) => ({ ...prev, existingWorkspaceKey: key }))}
        aria-pressed={selected}
        className={`w-full rounded-lg border px-4 py-4 text-left transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-calm ${
          selected
            ? "border-accent-calm bg-surface-hover shadow-hairline"
            : "border-line-faint bg-surface hover:border-accent-calm hover:bg-surface-hover"
        }`}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className={`font-display text-base ${selected ? "text-accent-calm" : "text-ink"}`}>
              {workspaceDisplayName(workspace)}
            </p>
            {ownerEmail && (
              <p className="mt-0.5 text-[13px] text-ink-soft">{ownerEmail}</p>
            )}
            <p className="mt-1 text-sm text-ink-soft">
              {describeWorkspace(workspace, category)}
            </p>
          </div>
          <span className="shrink-0 rounded-full border border-line px-2 py-1 text-[10px] uppercase tracking-[0.16em] text-ink-soft">
            {providerLabel(workspace.provider)}
          </span>
        </div>
      </button>
    );
  };

  /* ---------------------------------------------------------------- */
  /*  Starter project card                                              */
  /* ---------------------------------------------------------------- */

  const isStarterSelected = (kitId: string) =>
    draft.starterProject?.kind === "starter" && draft.starterProject.kitId === kitId;
  const isBlankSelected = draft.starterProject?.kind === "blank";

  /* ---------------------------------------------------------------- */
  /*  Review summary row                                               */
  /* ---------------------------------------------------------------- */

  const SummaryRow = ({
    label,
    value,
    detail,
    stepIndex,
  }: {
    label: string;
    value: string;
    detail?: string;
    stepIndex: number;
  }) => (
    <div className="flex items-start gap-4 py-4 border-b border-line-faint last:border-0">
      <p className="w-28 shrink-0 pt-0.5 font-mono text-[11px] uppercase tracking-[0.16em] text-ink-muted">{label}</p>
      <div className="min-w-0 flex-1">
        <p className="font-display text-[0.95rem] leading-tight text-ink">{value}</p>
        {detail && <p className="mt-1 text-xs leading-relaxed text-ink-muted">{detail}</p>}
      </div>
      <button
        type="button"
        onClick={() => goTo(stepIndex)}
        className="shrink-0 rounded-full border border-line-faint px-3 py-1 text-xs font-medium text-ink-soft transition-colors hover:border-accent-calm hover:text-accent-calm focus-visible:border-accent-calm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-calm"
      >
        Edit
      </button>
    </div>
  );

  const SetupSummaryStrip = ({
    title = "Current setup",
    items,
  }: {
    title?: string;
    items: Array<{ label: string; value: string } | null>;
  }) => {
    const visibleItems = items.filter((item): item is { label: string; value: string } => Boolean(item));
    if (visibleItems.length === 0) return null;

    return (
      <div className="mt-5 border-t border-line-faint pt-3">
        <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-ink-soft">
          {title}
        </p>
        <div className="mt-2 space-y-1.5">
          {visibleItems.map((item) => (
            <div
              key={`${item.label}:${item.value}`}
              className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5"
            >
              <span className="font-mono text-[11px] uppercase tracking-[0.16em] text-ink-muted">
                {item.label}
              </span>
              <span className="text-sm text-ink">
                {item.value}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const StartingPointNote = ({ children }: { children: React.ReactNode }) => (
    <Note variant="tip" className="onboarding-starting-point mt-4">
      <strong>Starting point:</strong> {children}
    </Note>
  );

  const starterProjectLabel = draft.starterProject
    ? draft.starterProject.kind === "starter"
      ? `${draft.starterProject.kitName}${starterKeepGit ? " (clone)" : ""}`
      : "Blank Project"
    : "Not selected";
  const defaultStarterKit = pickDefaultStarterKit(starterKits);
  const defaultProject = projects[0] || null;
  const ownedWorkspaceMatch = (() => {
    if (!userEmail) return null;
    return mine.find((ws) => {
      const wsEmail = typeof ws.meta?.ownerEmail === "string" ? ws.meta.ownerEmail.trim() : "";
      return wsEmail === userEmail;
    }) || null;
  })();
  const allWorkspaceChoices = [selectedWorkspace, ...personalWorkspaces, ...sharedProjectWorkspaces]
    .filter((row): row is WorkspaceRef => Boolean(row));
  const chosenExistingWorkspace =
    allWorkspaceChoices.find((row) => workspaceKey(row) === draft.existingWorkspaceKey) || null;

  return (
    <div className="min-h-[100dvh] bg-page text-ink">
      {/* Subtle gradient orb behind the card */}
      <div className="pointer-events-none fixed left-1/2 top-1/3 -translate-x-1/2 -translate-y-1/2">
        <div className="h-[600px] w-[600px] rounded-full bg-fwf-sky/5 blur-[120px]" />
      </div>

      <div className="relative mx-auto flex min-h-[100dvh] max-w-2xl flex-col items-center px-5 py-10 sm:px-8 sm:py-16">
        {/* Header */}
        <div className="mb-10 w-full text-center">
          <div className="mb-6 inline-flex items-center gap-2.5 rounded-full border border-line-faint bg-surface px-4 py-1.5">
            <span className="font-mono text-[11px] uppercase tracking-[0.16em] text-fwf-yellow">
              Fresh setup
            </span>
            <span className="h-1 w-1 rounded-full bg-fwf-yellow/60" />
            <span className="text-xs text-ink-soft">
              Step {step + 1} of {STEPS.length}
            </span>
          </div>

          <Stepper
            aria-label="Onboarding steps"
            current={step}
            steps={STEPS.map(({ label }) => label)}
          />
        </div>

        {/* Step content area */}
        <div className="relative w-full flex-1">
          {/* ---- STEP 0: Agent ---- */}
          <StepPanel active={step === 0} direction={direction}>
            <section>
              <h2 className="font-display text-2xl font-semibold text-ink sm:text-3xl">
                Pick your AI agent
              </h2>
              <p className="fwfa-subtitle mt-3">
                Which account do you want to sign in with?
              </p>
              <StartingPointNote>
                  Claude is preselected to get most people started quickly. You can pick a different agent at any time.
              </StartingPointNote>

              <div className="mt-6 grid gap-3 sm:grid-cols-2">
                {AGENT_ORDER
                  .filter((id) => id in AGENT_PRESETS)
                  .map((id) => {
                    const preset = AGENT_PRESETS[id];
                    const selected = draft.agentId === id;
                    const isVertex =
                      (id === "claude" && vertexFlags.claudeVertex) ||
                      (id === "gemini" && vertexFlags.geminiVertex);
                    const description = isVertex ? "No login needed, using Google Vertex." : preset.description;
                    const authLabel = isVertex ? "Google Vertex" : preset.requiresAuth;
                    const isRecommended = id === "claude";
                    return (
                      <button
                        key={id}
                        type="button"
                        data-testid={`onboarding-agent-${id}`}
                        onClick={() => onDraftChange((prev) => ({ ...prev, agentId: id }))}
                        aria-pressed={selected}
                        className={`rounded-lg border px-4 py-4 text-left transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-calm ${
                          selected
                            ? "border-accent-calm bg-surface-hover shadow-hairline"
                            : "border-line-faint bg-surface hover:border-accent-calm hover:bg-surface-hover"
                        }`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-display text-base text-ink">
                                {preset.name}
                              </p>
                              {isRecommended && (
                                <Badge variant="soft">
                                  Recommended
                                </Badge>
                              )}
                            </div>
                            <p className="mt-1.5 text-xs leading-relaxed text-ink-soft">
                              {description}
                            </p>
                          </div>
                          <ChoiceIndicator selected={selected} />
                        </div>
                        <p className="mt-3 text-[11px] uppercase tracking-[0.16em] text-ink-soft">
                          {authLabel}
                        </p>
                      </button>
                    );
                  })}
              </div>

              <SetupSummaryStrip
                items={[
                  draft.agentId
                    ? { label: "Agent", value: AGENT_PRESETS[draft.agentId]?.name ?? draft.agentId }
                    : null,
                ]}
              />

              <StepActionBar
                continueDisabled={!agentReady}
                continueTestId="onboarding-continue-agent"
                onContinue={() => goTo(1)}
              />
            </section>
          </StepPanel>

          {/* ---- STEP 1: Starter Project ---- */}
          <StepPanel active={step === 1} direction={direction}>
            <section>
              <h2 className="font-display text-2xl font-semibold text-ink sm:text-3xl">
                Choose a starter project
              </h2>
              <p className="fwfa-subtitle mt-3">
                Pick a template or start from scratch.
              </p>
              <StartingPointNote>
                  {defaultProject
                    ? `${defaultProject.name} is selected for you, and ${defaultStarterKit?.name || "the default starter"} is ready as a starting point.`
                    : `${defaultStarterKit?.name || "A starter project"} is preselected so you can keep moving and adjust it if you want.`}
              </StartingPointNote>

              {/* Project name input */}
              <Field
                className="mb-6 mt-6"
                label="Project name"
                error={slugStatus === "reserved"
                  ? "This name is reserved. Please choose a different name."
                  : slugStatus === "taken"
                    ? "This name is already taken. Please choose a different name."
                    : undefined}
              >
                <div className="relative">
                  <Input
                    type="text"
                    value={draft.projectName}
                    onChange={(e) => onDraftChange((prev) => ({ ...prev, projectName: e.target.value }))}
                    placeholder="my-awesome-project"
                    error={slugStatus === "taken" || slugStatus === "reserved"}
                    className="pr-10"
                  />
                  {/* Status indicator inside input */}
                  {draft.projectName.trim() && (
                    <span className="absolute right-3 top-1/2 -translate-y-1/2">
                      {slugStatus === "checking" && (
                        <span className="h-3.5 w-3.5 block animate-spin rounded-full border-2 border-line-strong border-t-accent-calm" />
                      )}
                      {slugStatus === "available" && (
                        <svg className="h-4 w-4 text-success" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                        </svg>
                      )}
                      {(slugStatus === "taken" || slugStatus === "reserved") && (
                        <svg className="h-4 w-4 text-danger" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                        </svg>
                      )}
                    </span>
                  )}
                </div>
                {draft.projectName.trim() && (
                  <div className="mt-1.5 space-y-0.5">
                    <p className="text-xs text-ink-soft">
                      Folder: <span className="font-mono text-ink-soft">{sanitizeProjectFolderName(draft.projectName)}</span>
                    </p>
                    {portProxyDomain && (
                      <p className="text-xs text-ink-soft">
                        URL: <span className={`font-mono ${slugStatus === "taken" || slugStatus === "reserved" ? "text-danger" : "text-ink-soft"}`}>{sanitizeProjectFolderName(draft.projectName)}.{portProxyDomain}</span>
                      </p>
                    )}
                  </div>
                )}
              </Field>

              <div>
                {loadingStarterKits ? (
                  <div className="flex items-center gap-3 py-8 text-sm text-ink-muted">
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-line-strong border-t-accent-calm" />
                    Loading starter projects...
                  </div>
                ) : (
                  <div className="grid gap-3">
                    {starterKits.map((kit) => (
                      <div key={kit.id}>
                        <button
                          type="button"
                          onClick={() => handleSelectStarterKit(kit)}
                          aria-pressed={isStarterSelected(kit.id)}
                          className={`w-full rounded-lg border px-4 py-4 text-left transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-calm ${
                            isStarterSelected(kit.id)
                              ? "border-accent-calm bg-surface-hover shadow-hairline"
                              : "border-line-faint bg-surface hover:border-accent-calm hover:bg-surface-hover"
                          }`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex items-start gap-3">
                              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-surface-hover text-lg">
                                {kit.source === "github" ? (
                                  <svg className="h-5 w-5 text-accent-calm" viewBox="0 0 16 16" fill="currentColor">
                                    <path d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.013 8.013 0 0016 8c0-4.42-3.58-8-8-8z" />
                                  </svg>
                                ) : (
                                  kit.icon || "\uD83D\uDE80"
                                )}
                              </div>
                                <div>
                                  <div className="flex items-center gap-2">
                                    <p className="font-display text-base font-medium text-ink">
                                      {kit.name}
                                    </p>
                                    {defaultStarterKit?.id === kit.id && (
                                      <span className="rounded-full border border-line px-2 py-0.5 text-[11px] uppercase tracking-[0.16em] text-ink-soft">
                                        Default
                                      </span>
                                    )}
                                    {kit.source === "github" && kit.repoFullName && (
                                      <span className="rounded-full border border-line-faint px-1.5 py-0.5 text-[11px] text-ink-soft">
                                        {kit.repoFullName}
                                    </span>
                                  )}
                                </div>
                                <p className="mt-1.5 text-xs leading-relaxed text-ink-soft">
                                  {kit.description}
                                </p>
                              </div>
                            </div>
                            <ChoiceIndicator selected={isStarterSelected(kit.id)} />
                          </div>
                        </button>
                        {/* Keep git toggle — advanced option, self-revealing on hover */}
                        {isAdmin && isStarterSelected(kit.id) && kit.source === "github" && (() => {
                          const keepGit = starterKeepGit;
                          return (
                            <button
                              type="button"
                              onClick={handleToggleKeepGit}
                              className="mt-2 ml-12 flex items-center gap-2 text-[0.75rem] text-ink-muted transition-all duration-200 hover:text-ink-soft focus:text-ink-soft"
                              title="Advanced: toggle between template mode and full git clone"
                            >
                              <span className={`flex h-4 w-8 rounded-full transition-colors ${keepGit ? "bg-accent-calm" : "bg-surface-hover"}`}>
                                <span className={`mt-0.5 ml-0.5 h-3 w-3 rounded-full bg-ink transition-transform ${keepGit ? "translate-x-3.5" : ""}`} />
                              </span>
                              <span>{keepGit ? "Keep git history (clone)" : "Use as template (no git history)"}</span>
                            </button>
                          );
                        })()}
                      </div>
                    ))}

                    {/* Blank project option */}
                    <button
                      type="button"
                      onClick={handleSelectBlank}
                      aria-pressed={isBlankSelected}
                      className={`rounded-lg border px-4 py-4 text-left transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-calm ${
                        isBlankSelected
                          ? "border-accent-calm bg-surface-hover shadow-hairline"
                          : "border-line-faint bg-surface hover:border-accent-calm hover:bg-surface-hover"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3">
                          <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-surface-hover transition-colors ${isBlankSelected ? "text-accent-calm" : "text-ink-muted"}`}>
                            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                            </svg>
                          </div>
                          <div>
                            <p className="font-display text-base font-medium text-ink">
                              Blank Project
                            </p>
                            <p className="mt-1 text-xs leading-relaxed text-ink-soft">
                              Start fresh with an empty workspace
                            </p>
                          </div>
                        </div>
                        <ChoiceIndicator selected={isBlankSelected} />
                      </div>
                    </button>
                  </div>
                )}
              </div>

              <SetupSummaryStrip
                items={[
                  draft.agentId
                    ? { label: "Agent", value: AGENT_PRESETS[draft.agentId]?.name ?? draft.agentId }
                    : null,
                  draft.projectName.trim()
                    ? { label: "Folder", value: sanitizeProjectFolderName(draft.projectName) }
                    : null,
                  draft.projectName.trim() && portProxyDomain
                    ? { label: "URL", value: `${sanitizeProjectFolderName(draft.projectName)}.${portProxyDomain}` }
                    : null,
                  draft.starterProject
                    ? { label: "Starter", value: starterProjectLabel }
                    : null,
                ]}
              />

              <StepActionBar
                backTestId="onboarding-back-project"
                continueDisabled={!starterProjectReady}
                continueTestId="onboarding-continue-project"
                onBack={() => goTo(0)}
                onContinue={() => goTo(2)}
              />
            </section>
          </StepPanel>

          {/* ---- STEP 2: Workspace ---- */}
          <StepPanel active={step === 2} direction={direction}>
            <section>
              <h2 className="font-display text-2xl font-semibold text-ink sm:text-3xl">
                Choose your workspace
              </h2>
              <p className="fwfa-subtitle mt-3">
                Use an existing workspace or create a new one.
              </p>
              <StartingPointNote>
                  {ownedWorkspaceMatch && draft.workspaceMode === "existing" && draft.existingWorkspaceKey === workspaceKey(ownedWorkspaceMatch)
                    ? "We found an existing workspace that appears to belong to you, so it's selected as the fastest path."
                    : "If you already have a workspace, you can reuse it. Otherwise FreshVibe can create a new one for you."}
              </StartingPointNote>

              {/* Mode toggle */}
              <div className="mt-6 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => onDraftChange((prev) => ({ ...prev, workspaceMode: "existing" }))}
                  aria-pressed={draft.workspaceMode === "existing"}
                  className={`rounded-full px-4 py-2 text-sm font-semibold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-calm ${
                    draft.workspaceMode === "existing"
                      ? "border border-accent-calm bg-surface-hover text-ink shadow-hairline"
                      : "border border-line-faint bg-surface text-ink-soft hover:border-accent-calm hover:bg-surface-hover hover:text-accent-calm"
                  }`}
                >
                  Existing workspace
                </button>
                <button
                  type="button"
                  onClick={() => onDraftChange((prev) => ({ ...prev, workspaceMode: "create" }))}
                  aria-pressed={draft.workspaceMode === "create"}
                  className={`rounded-full px-4 py-2 text-sm font-semibold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-calm ${
                    draft.workspaceMode === "create"
                      ? "border border-accent-calm bg-surface-hover text-ink shadow-hairline"
                      : "border border-line-faint bg-surface text-ink-soft hover:border-accent-calm hover:bg-surface-hover hover:text-accent-calm"
                  }`}
                >
                  Create new
                </button>
              </div>

              <div className="mt-5">
                {draft.workspaceMode === "existing" ? (
                  <div className="space-y-4">
                    {loadingProjectRows && (
                      <div className="flex items-center gap-3 text-sm text-ink-muted">
                        <span className="h-4 w-4 animate-spin rounded-full border-2 border-line-strong border-t-accent-calm" />
                        Loading workspaces...
                      </div>
                    )}

                    {selectedWorkspace && (
                      <div>
                        <p className="mb-2 font-mono text-[13px] uppercase tracking-[0.16em] text-ink-muted">
                          Selected workspace
                        </p>
                        {renderWorkspaceCard(selectedWorkspace, "selected")}
                      </div>
                    )}

                    {personalWorkspaces.length > 0 && (
                      <div>
                        <p className="mb-4 font-mono text-[13px] uppercase tracking-[0.16em] text-ink-muted">
                          Your workspaces
                        </p>
                        <div className="space-y-2">
                          {personalWorkspaces.map((ws) => renderWorkspaceCard(ws, "mine"))}
                        </div>
                      </div>
                    )}

                    {sharedProjectWorkspaces.length > 0 && (
                      <div>
                        <p className="mb-2 font-mono text-[13px] uppercase tracking-[0.16em] text-ink-muted">
                          Project workspaces
                        </p>
                        <div className="space-y-2">
                          {sharedProjectWorkspaces.map((ws) => renderWorkspaceCard(ws, "project"))}
                        </div>
                      </div>
                    )}

                    {!selectedWorkspace && personalWorkspaces.length === 0 && sharedProjectWorkspaces.length === 0 && (
                      <div className="rounded-lg border border-dashed border-line-faint bg-surface px-4 py-5 text-center">
                        <p className="font-display text-lg text-ink">No matching workspaces yet</p>
                        <p className="mt-2 text-sm text-ink-soft">
                          Create a fresh workspace and FreshVibe will finish the rest automatically.
                        </p>
                        <button
                          type="button"
                          onClick={() => onDraftChange((prev) => ({ ...prev, workspaceMode: "create" }))}
                          className="mt-4 rounded-full border border-ink bg-ink px-4 py-2 text-xs font-semibold text-primary shadow-soft transition-all hover:border-primary-hover hover:bg-primary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-calm"
                        >
                          Create a new workspace
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="rounded-lg border border-line-faint bg-surface p-5">
                      <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-ink-muted">Provider</p>
                      <div className="mt-4 grid gap-3 sm:grid-cols-2">
                        {availableProviders.map((provider) => {
                          const selected = draft.createProvider === provider;
                          return (
                            <button
                              key={provider}
                              type="button"
                              onClick={() => onDraftChange((prev) => ({ ...prev, createProvider: provider }))}
                              aria-pressed={selected}
                              className={`rounded-lg border px-4 py-4 text-left transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-calm ${
                                selected
                                  ? "border-accent-calm bg-surface-hover shadow-hairline"
                                  : "border-line-faint bg-surface hover:border-accent-calm hover:bg-surface-hover"
                              }`}
                            >
                              <p className={`font-display text-base text-ink`}>
                                {providerLabel(provider)}
                              </p>
                              <p className="mt-1.5 text-xs leading-relaxed text-ink-soft">
                                {provider === "gcp_compute"
                                  ? "Persistent compute runtime with storage-backed workspaces."
                                  : provider === "e2b"
                                    ? "Isolated E2B sandbox runtime."
                                    : provider === "daytona"
                                      ? "Isolated Daytona sandbox runtime."
                                      : "Fast sandbox runtime for quick cloud setup."}
                              </p>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Advanced: share toggle — self-revealing on hover */}
                    <label
                      className="flex cursor-pointer items-center gap-3 rounded-lg border border-transparent px-4 py-3 text-sm text-ink-muted transition-all duration-200 hover:border-line-faint hover:bg-surface hover:text-ink-soft focus-within:border-line-faint focus-within:bg-surface focus-within:text-ink-soft"
                      title="Advanced: control whether teammates can see this workspace"
                    >
                      <span className={`flex h-4 w-8 shrink-0 rounded-full transition-colors ${draft.createShared ? "bg-accent-calm" : "bg-surface-hover"}`}>
                        <span className={`mt-0.5 ml-0.5 h-3 w-3 rounded-full bg-ink transition-transform ${draft.createShared ? "translate-x-3.5" : ""}`} />
                      </span>
                      <input
                        type="checkbox"
                        checked={draft.createShared}
                        onChange={(e) => onDraftChange((prev) => ({ ...prev, createShared: e.target.checked }))}
                        className="sr-only"
                      />
                      <span>
                        {draft.createShared ? "Shared with project" : "Private workspace"}
                      </span>
                    </label>
                  </div>
                )}
              </div>

              <SetupSummaryStrip
                items={[
                  draft.agentId
                    ? { label: "Agent", value: AGENT_PRESETS[draft.agentId]?.name ?? draft.agentId }
                    : null,
                  draft.starterProject
                    ? { label: "Starter", value: starterProjectLabel }
                    : null,
                  draft.workspaceMode === "existing"
                    ? chosenExistingWorkspace
                      ? { label: "Workspace", value: workspaceDisplayName(chosenExistingWorkspace) }
                      : { label: "Workspace", value: "Choose existing" }
                    : { label: "Workspace", value: `New ${providerLabel(draft.createProvider)}` },
                ]}
              />

              <StepActionBar
                backTestId="onboarding-back-workspace"
                continueDisabled={!workspaceReady}
                continueTestId="onboarding-continue-workspace"
                onBack={() => goTo(1)}
                onContinue={() => goTo(3)}
              />
            </section>
          </StepPanel>

          {/* ---- STEP 3: Review & Launch ---- */}
          <StepPanel active={step === 3} direction={direction}>
            <section>
              <div className="inline-flex items-center gap-2 rounded-full border border-line-faint bg-surface-hover px-3 py-1">
                <span className="font-mono text-[11px] uppercase tracking-[0.16em] text-accent-calm">
                  Final Review
                </span>
              </div>
              <h2 className="mt-4 font-display text-2xl font-semibold text-ink sm:text-3xl">
                Ready to launch
              </h2>
              <p className="fwfa-subtitle mt-3">
                Review your setup before launching your workspace. You can still edit any section below.
              </p>

              <div className="mt-8 rounded-2xl border border-line-faint bg-surface px-5 py-3 shadow-hairline">
                <div className="mb-2 flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full bg-accent-calm" />
                  <p className="font-mono text-xs uppercase tracking-[0.16em] text-ink-soft">
                    Summary
                  </p>
                </div>
                <SummaryRow
                  label="Agent"
                  value={draft.agentId ? (AGENT_PRESETS[draft.agentId]?.name ?? draft.agentId) : "Not selected"}
                  detail={draft.agentId ? AGENT_PRESETS[draft.agentId]?.requiresAuth : undefined}
                  stepIndex={0}
                />
                <SummaryRow
                  label="Project name"
                  value={draft.projectName.trim() || "Not set"}
                  detail={
                    draft.projectName.trim()
                      ? portProxyDomain
                        ? `Folder: ${sanitizeProjectFolderName(draft.projectName)} · URL: ${sanitizeProjectFolderName(draft.projectName)}.${portProxyDomain}`
                        : `Folder: ${sanitizeProjectFolderName(draft.projectName)}`
                      : undefined
                  }
                  stepIndex={1}
                />
                <SummaryRow
                  label="Starter project"
                  value={starterProjectLabel}
                  detail={draft.starterProject?.kind === "starter"
                    ? starterKeepGit
                      ? "Repository history will be preserved in your workspace"
                      : "Template files will be copied into your workspace"
                    : undefined}
                  stepIndex={1}
                />
                <SummaryRow
                  label="Workspace"
                  value={
                    draft.workspaceMode === "existing"
                      ? draft.existingWorkspaceKey
                        ? "Connect to existing workspace"
                        : "Not selected"
                      : `Create new ${providerLabel(draft.createProvider)} workspace`
                  }
                  detail={
                    draft.workspaceMode === "create" && draft.createShared
                      ? `Shared with ${selectedProject?.name || "project"}`
                      : draft.workspaceMode === "create"
                        ? "Private workspace"
                        : undefined
                  }
                  stepIndex={2}
                />
              </div>

              {(loadError || error) && (
                <Status className="mt-5" tone="danger" alert>
                  {loadError || error}
                </Status>
              )}

              <StepActionBar
                backTestId="onboarding-back-launch"
                continueBusy={submitting}
                continueBusyLabel="Setting things up..."
                continueDisabled={!canStart}
                continueLabel="Launch workspace"
                continueStretch
                continueTestId="onboarding-launch-workspace"
                onBack={() => goTo(2)}
                onContinue={() => void handleStart()}
              />
            </section>
          </StepPanel>
        </div>

        {/* Spacer — bottom navigation is now inside each card */}
        <div className="mt-8" />
      </div>
    </div>
  );
}
